/**
 * AI CONTEXT
 * Purpose: manager-level Firebase auth gate (separate from pin/user identity).
 * Exposes: isManagerLoggedIn + manager login/logout actions.
 * Side effects: listens to Firebase auth state changes.
 * Used by: Login flow and App redirect logic.
 * Refactor: remove console noise and centralize auth error handling/reporting.
 */
import { App } from "antd"
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

import FirebaseAuth from "#classes/FirebaseAuth"

const DEBUG_MODE: boolean = import.meta.env.VITE_DEBUG_MODE === "true"
if (DEBUG_MODE) {
  console.log("✅🔥 DEBUG_MODE ACTIVE => ", DEBUG_MODE)
}
interface AuthorizationContextType {
  isManagerLoggedIn: boolean
  onManagerLogin: (values: {
    username: string
    password: string
  }) => Promise<void>
  onManagerLogout: () => void
}

// Used to authenticate for Firebase services. User level auth is in UserContextProvider.
export const AuthorizationContext = createContext<AuthorizationContextType>({
  isManagerLoggedIn: false,
  onManagerLogin: async () => {},
  onManagerLogout: () => {},
})

const AuthorizationContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [isManagerLoggedIn, setIsManagerLoggedIn] = useState(DEBUG_MODE)

  const { modal } = App.useApp()

  useEffect(() => {
    const unsubscribe = FirebaseAuth.getInstance().setAuthStateListener(
      (user) => {
        console.log("✅ Manager login successful!")
        setIsManagerLoggedIn(!!user)
      },
      (error: string) => {
        modal.error({
          title: "Error while checking authorization",
          content: error,
        })
      }
    )
    return () => unsubscribe?.()
  }, [modal])

  const onManagerLogin = async (values: {
    username: string
    password: string
  }): Promise<void> => {
    const response =
      await FirebaseAuth.getInstance().signInWithEmailAndPassword(
        values.username,
        values.password
      )
    if (!response.user?.uid) throw new Error("Invalid username or password")
  }

  const onManagerLogout = () => {
    FirebaseAuth.getInstance()
      .signOut()
      .then(() => {
        setIsManagerLoggedIn(false)
        console.log("✅ Signed out successfully")
      })
  }

  const value = {
    isManagerLoggedIn,
    onManagerLogout,
    onManagerLogin,
  }

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  )
}

export const useAuthorizationContext = () => useContext(AuthorizationContext)

export default AuthorizationContextProvider
